import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import './AdicionalCatalogModal.css';

export type AdicionalModalKind = 'management' | 'superintendency' | 'area' | 'service_reason';

interface AdicionalCatalogModalProps {
  isOpen: boolean;
  onClose: () => void;
  kind: AdicionalModalKind;
  onSubmit: (payload: { name?: string; area_responsible?: string; description?: string }) => void;
  loading?: boolean;
}

const LABELS: Record<AdicionalModalKind, { title: string; primaryLabel: string; primaryPlaceholder: string; showSecondary?: boolean; secondaryLabel?: string }> = {
  management: { title: 'Nueva gerencia', primaryLabel: 'Nombre', primaryPlaceholder: 'Nombre de la gerencia' },
  superintendency: { title: 'Nueva superintendencia', primaryLabel: 'Nombre', primaryPlaceholder: 'Nombre de la superintendencia' },
  area: {
    title: 'Nueva área',
    primaryLabel: 'Nombre',
    primaryPlaceholder: 'Nombre del área',
    showSecondary: true,
    secondaryLabel: 'Responsable del área',
  },
  service_reason: {
    title: 'Nuevo motivo del servicio',
    primaryLabel: 'Descripción',
    primaryPlaceholder: 'Descripción del motivo',
  },
};

const AdicionalCatalogModal: React.FC<AdicionalCatalogModalProps> = ({
  isOpen,
  onClose,
  kind,
  onSubmit,
  loading = false,
}) => {
  const config = LABELS[kind];
  const isTextArea = kind === 'service_reason';
  const [primary, setPrimary] = useState('');
  const [secondary, setSecondary] = useState('');

  useEffect(() => {
    if (isOpen) {
      setPrimary('');
      setSecondary('');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = primary.trim();
    if (!trimmed) return;
    if (kind === 'service_reason') {
      onSubmit({ description: trimmed });
    } else if (kind === 'area') {
      onSubmit({ name: trimmed, area_responsible: secondary.trim() || undefined });
    } else {
      onSubmit({ name: trimmed });
    }
    // El padre cierra el modal tras éxito del store
  };

  if (!isOpen) return null;

  return (
    <div className="adicional-modal-overlay" onClick={onClose}>
      <div className="adicional-modal-content" onClick={e => e.stopPropagation()}>
        <div className="adicional-modal-header">
          <h3 className="adicional-modal-title">{config.title}</h3>
          <button type="button" className="adicional-modal-close" onClick={onClose} aria-label="Cerrar">
            <X size={22} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="adicional-modal-form">
          <div className="adicional-modal-field">
            <label>{config.primaryLabel}</label>
            {isTextArea ? (
              <textarea
                value={primary}
                onChange={e => setPrimary(e.target.value)}
                placeholder={config.primaryPlaceholder}
                rows={3}
                required
                disabled={loading}
              />
            ) : (
              <input
                type="text"
                value={primary}
                onChange={e => setPrimary(e.target.value)}
                placeholder={config.primaryPlaceholder}
                required
                disabled={loading}
              />
            )}
          </div>
          {config.showSecondary && (
            <div className="adicional-modal-field">
              <label>{config.secondaryLabel}</label>
              <input
                type="text"
                value={secondary}
                onChange={e => setSecondary(e.target.value)}
                placeholder="Nombre del responsable"
                disabled={loading}
              />
              {kind === 'area' && (
                <p className="adicional-modal-hint">
                  Puede repetirse el nombre del área si el responsable es distinto. No se permite el
                  mismo área con el mismo responsable dos veces.
                </p>
              )}
            </div>
          )}
          <div className="adicional-modal-actions">
            <button type="button" className="adicional-modal-btn secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="adicional-modal-btn primary" disabled={loading || !primary.trim()}>
              {loading ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdicionalCatalogModal;
